import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Check, Shield, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import shomriLogo from '@/assets/shomri-logo.svg';

const ChildSelection = () => {
  const { device } = useAuth();
  const navigate = useNavigate();

  const handleDirectProceed = () => {
    navigate('/device-detection');
  };

  const handleMentorInfo = () => {
    navigate('/mentor-invite-info');
  };

  return (
    <div className="min-h-screen w-full bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-2xl bg-card rounded-3xl shadow-xl overflow-hidden border border-border p-12 relative">
        {/* Decorative gradient with Shomri colors */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-accent/15 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
        <div className="absolute top-1/2 left-0 w-32 h-32 bg-[hsl(var(--shomri-blue))]/10 rounded-full blur-2xl -translate-x-1/2" />
        
        <div className="relative z-10 text-center">
          {/* Shomri Logo */}
          <div className="mb-6">
            <img src={shomriLogo} alt="Shomri" className="h-8 mx-auto" />
          </div>

          {/* Success badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full mb-8">
            <div className="w-5 h-5 bg-primary rounded-full flex items-center justify-center">
              <Check className="w-3 h-3 text-white" strokeWidth={3} />
            </div>
            <span className="text-sm font-semibold text-primary">Code verified</span>
          </div>

          {/* Welcome text */}
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-3 tracking-tight">
            Welcome, {device?.user?.firstName || 'User'}
          </h1>
          
          <p className="text-lg text-muted-foreground mb-10 max-w-md mx-auto">
            You're ready to set up protection on your device
          </p>

          {/* Feature highlights */}
          <div className="flex flex-col sm:flex-row justify-center gap-4 mb-10">
            <div className="flex items-center gap-3 px-5 py-4 bg-muted/50 rounded-2xl">
              <div className="w-11 h-11 bg-primary/15 rounded-xl flex items-center justify-center">
                <Shield className="w-5 h-5 text-primary" />
              </div>
              <div className="text-left">
                <p className="text-sm font-semibold text-foreground">Safe Browsing</p>
                <p className="text-xs text-muted-foreground">Protection enabled</p>
              </div>
            </div>
            <div className="flex items-center gap-3 px-5 py-4 bg-muted/50 rounded-2xl">
              <div className="w-11 h-11 bg-accent/20 rounded-xl flex items-center justify-center">
                <Clock className="w-5 h-5 text-accent" />
              </div>
              <div className="text-left">
                <p className="text-sm font-semibold text-foreground">Quick Setup</p>
                <p className="text-xs text-muted-foreground">~2 min to complete</p>
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex flex-col gap-3 max-w-sm mx-auto">
            <Button 
              onClick={handleDirectProceed} 
              size="lg"
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold py-6 rounded-2xl shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transition-all duration-300 group text-base"
            >
              Continue to Device Setup
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            
            {device?.user?.identity === 'mentee' && (
              <Button 
                onClick={handleMentorInfo} 
                variant="ghost"
                size="lg"
                className="w-full py-6 rounded-2xl text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-300"
              >
                Update mentor info
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChildSelection;
